import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Col, Form, Row } from 'antd';

import { valueToNumber, valueToString } from '@/utilz';
import { saveShipmentProductInProgress } from '@/redux/shipment/ShipmentActions';
import { routeFileSelector } from '@/utilz/i18n';
import FormInput from '@/views/components/Inputs/FormInput';
import FormTextArea from '@/views/components/Inputs/FormTextArea';
import ClaimsCommodityCodeSelector from '@/views/components/Selectors/ClaimsCommodityCodeSelector';
import ClaimsLidLabelSelector from '@/views/components/Selectors/ClaimsLidLabelSelector';
import ProductUnitUomSelector from '@/views/components/Selectors/ProductUnitUomSelector';

import { RegularReleaseSelect } from '../../PreClaims/components/selects';

const AddProductFullForm = ({ formInstance = false, handleSuccess }) => {
  const dispatch = useDispatch();
  const { selectedCompany, user } = useSelector((state) => state.auth);
  let [form] = Form.useForm();
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  if (formInstance) {
    form = formInstance;
  }

  const handleSubmit = (data) => {
    const payload = {
      ...data,
      callback: handleCallback,
      claimantValue: valueToNumber(data.claimantValue),
      commodityCodeKey: valueToNumber(data.commodityCodeKey),
      createdBy: user?.data?.id,
      customerKey: selectedCompany?.companyId,
      fullRelFlag: valueToString(data?.fullRelFlag),
      hazmatUnNumber: valueToString(data?.hazmatUnNumber),
      lidLabelKey: valueToNumber(data.lidLabelKey),
      nmfcClassNumber: valueToString(data?.nmfcClassNumber),
      optimizationIdentifier: valueToString(data?.optimizationIdentifier),
      palletVolume: valueToNumber(data.palletVolume),
      palletWeight: valueToNumber(data.palletWeight),
      productUnitMeasure: valueToString(data?.productUnitMeasure),
      productWeight: valueToNumber(data.productWeight),
      releaseValue: valueToNumber(data.releaseValue),
      shipRecKey: valueToNumber(data.shipRecKey),
    };
    dispatch(saveShipmentProductInProgress(payload));
  };

  const handleCallback = (data) => {
    form.resetFields();
    if (handleSuccess) {
      handleSuccess(data);
    }
  };

  return (
    <Form
      form={form}
      onFinish={handleSubmit}
      initialValues={{
        claimantValue: undefined,
        commodityCodeKey: undefined,
        fullRelFlag: ' ',
        hazmatUnNumber: undefined,
        lidLabelKey: undefined,
        nmfcClassNumber: undefined,
        optimizationIdentifier: undefined,
        palletVolume: undefined,
        palletWeight: undefined,
        productUnitMeasure: undefined,
        productWeight: undefined,
        releaseValue: undefined,
        shipRecKey: undefined,
      }}
    >
      <Row gutter={30}>
        <Col md={8}>
          <FormInput
            name="productCode"
            label={t('translation:default.fields.productCode.label')}
            editMode
            validateTrigger={['onChange', 'onBlur']}
            rules={[
              {
                message: t('translation:default.fields.productCode.message'),
                required: true,
              },
            ]}
            maxLength={25}
            margin="mb-10"
          />
          <FormTextArea
            name="productDesc"
            label={t('translation:default.fields.productDesc.label')}
            editMode
            rows={3}
            margin="mb-10"
            rules={[
              {
                message: 'Please enter Product Description',
                required: true,
              },
            ]}
            maxLength={150}
          />
          <ProductUnitUomSelector
            margin="mb-10"
            label="Unit Of Measure"
            placeholder="Select A Unit Of Measure"
            name="productUnitMeasure"
            editMode
          />
          <RegularReleaseSelect
            margin="mb-10"
            label="Full/Release Value"
            placeholder="Select A Full/Release Value"
            name="fullRelFlag"
            editMode
          />
          <FormInput
            margin="m-0"
            label="Release"
            placeholder="Select A Release"
            name="releaseValue"
            editMode
            inputType="number"
          />
        </Col>
        <Col md={8}>
          <FormInput
            margin="mb-10"
            label="Claimant Value"
            placeholder="Enter Claimant Value"
            name="claimantValue"
            editMode
            inputType="number"
          />
          <FormInput
            margin="mb-10"
            label="Chargeable Weight"
            placeholder="Enter Chargeable Weight"
            name="productWeight"
            editMode
            inputType="number"
          />
          <FormInput
            margin="mb-10"
            label="NMFC Class"
            placeholder="Enter NMFC Class"
            name="nmfcClassNumber"
            editMode
            maxLength={25}
          />
          <ClaimsCommodityCodeSelector margin="mb-10" name="commodityCodeKey" editMode />
          <FormInput
            margin="m-0"
            label="Pallet Weight"
            placeholder="Enter Pallet Weight"
            name="palletWeight"
            editMode
            inputType="number"
          />
        </Col>
        <Col md={8}>
          <FormInput
            margin="mb-10"
            label="Pallet Volumn"
            placeholder="Enter Pallet Volumn"
            name="palletVolume"
            editMode
            inputType="number"
          />
          <ClaimsLidLabelSelector margin="mb-10" name="lidLabelKey" editMode />
          <FormInput
            margin="mb-10"
            label="Optimization ID"
            placeholder="Enter Optimization ID"
            name="optimizationIdentifier"
            editMode
          />
          <FormInput
            margin="mb-10"
            label="Ship/Rec Key"
            placeholder="Enter Ship/Rec Key"
            name="shipRecKey"
            editMode
            inputType="number"
          />
          <FormInput
            margin="m-0"
            label="HAZMAT UN Number"
            placeholder="Enter HAZMAT UN Number"
            name="hazmatUnNumber"
            editMode
            maxLength={10}
          />
        </Col>
      </Row>
    </Form>
  );
};

export default AddProductFullForm;
